// src/routes/risk/index.tsx
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { motion } from 'framer-motion';
import { riskStore, riskActions } from '../../stores/riskStore';
import { assetStore, assetActions } from '../../stores/assetStore';
import { getUniqueAssetGroups, getGroupCategoryCount } from '../../stores/assetStore';
import { getAssetDisplayName } from '../../utils/assetDisplayName';
import { adminStore } from '../../stores/adminStore';
import { globalConfigStore } from '../../stores/globalConfigStore';
import { riskMatrixStore, riskMatrixActions } from '../../stores/riskMatrixStore';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { PageContainer, ContentWrapper } from '../../components/layout/PageContainer';
import { ModuleHeader } from '../../components/layout/ModuleHeader';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { AiSuggestButton } from '../../components/ui/AiSuggestButton';
import { AiControlSuggest } from '../../components/ui/AiControlSuggest';
import { formatDate } from '../../utils/dateUtils';
import { exportToCSV, prepareRiskExportData } from '../../utils/exportUtils';
import { getImplementationWording, getOwners, getLocations, getThreatsByCategory, getVulnerabilitiesByThreat } from '../../stores/adminStore';
import { assetCategoryOptions } from '../../utils/dropdownData';
import { iso27001Categories } from '../../data/assetCategories';
import type { Risk, RiskFormData, RiskControl, Likelihood, Impact, Severity } from '../../types/risk';
import type { Asset } from '../../types/asset';

export const Route = createFileRoute('/risk/')({
  component: RiskAssessmentModule,
});

// Risk calculation with 5x5 matrix - values now dynamic from riskMatrixStore
function calculateRiskScore(likelihood: string, severity: string): { score: number; level: string; color: string } {
  // Get dynamic labels from riskMatrixStore
  const likelihoodLabels = riskMatrixStore.infoSec.likelihoodLabels.map(l => l.label);
  const severityLabels = riskMatrixStore.infoSec.impactLabels.map(l => l.label);
  
  // Calculate dynamic values based on position in array
  const likelihoodValues: Record<string, number> = {};
  likelihoodLabels.forEach((label, idx) => {
    likelihoodValues[label] = idx + 1;
  });
  
  const severityValues: Record<string, number> = {};
  severityLabels.forEach((label, idx) => {
    severityValues[label] = idx + 1;
  });
  
  const l = likelihoodValues[likelihood] || 3;
  const s = severityValues[severity] || 3;
  const score = l * s;
  
  // Get color from matrix store using dynamic label positions
  const likelihoodIdx = likelihoodLabels.indexOf(likelihood);
  const severityIdx = severityLabels.indexOf(severity);
  
  let matrixColor: 'green' | 'amber' | 'red' = 'green';
  let level = 'Low';
  let color = '#16a34a'; // green
  
  // Get color from riskMatrixStore
  try {
    if (likelihoodIdx >= 0 && severityIdx >= 0) {
      if (riskMatrixStore.infoSec && riskMatrixStore.infoSec.colors[likelihoodIdx]) {
        matrixColor = riskMatrixStore.infoSec.colors[likelihoodIdx][severityIdx];
      }
    }
  } catch (e) {
    // Fallback to score-based calculation
    if (score >= 15) matrixColor = 'red';
    else if (score >= 5) matrixColor = 'amber';
    else matrixColor = 'green';
  }
  
  // Map matrix color to level and hex color
  if (matrixColor === 'red') {
    level = 'High';
    color = '#dc2626';
  } else if (matrixColor === 'amber') {
    level = 'Medium';
    color = '#f59e0b';
  } else {
    level = 'Low';
    color = '#16a34a';
  }
  
  return { score, level, color };
}

// Dynamic labels are now loaded from riskMatrixStore in components

// Risk Matrix Component for InfoSec - No numbers, better spacing, with descriptions and RAG key
function InfoSecRiskMatrix({ 
  selectedLikelihood, 
  selectedSeverity, 
  onSelect, 
  title,
  accentColor = 'gray-800'
}: { 
  selectedLikelihood: string; 
  selectedSeverity: string; 
  onSelect: (likelihood: string, severity: string) => void;
  title: string;
  accentColor?: string;
}) {
  const matrixSnap = useSnapshot(riskMatrixStore);
  const [showLikelihoodDesc, setShowLikelihoodDesc] = useState(false);
  const [showSeverityDesc, setShowSeverityDesc] = useState(false);

  // Get dynamic labels from matrix store
  const likelihoodLabels = matrixSnap.infoSec.likelihoodLabels.map(l => l.label);
  const severityLabels = matrixSnap.infoSec.impactLabels.map(l => l.label);
  
  // Create dynamic value mappings
  const likelihoodValues: Record<string, number> = {};
  likelihoodLabels.forEach((label, idx) => {
    likelihoodValues[label] = idx + 1;
  });

  const severityValues: Record<string, number> = {};
  severityLabels.forEach((label, idx) => {
    severityValues[label] = idx + 1;
  });

  const getRiskColorForCell = (likelihoodIdx: number, severityIdx: number): string => {
    const color = matrixSnap.infoSec.colors[likelihoodIdx][severityIdx];
    return riskMatrixActions.getColorClass(color);
  };

  const isSelected = (likelihoodIdx: number, severityIdx: number): boolean => {
    return likelihoodLabels[likelihoodIdx] === selectedLikelihood && 
           severityLabels[severityIdx] === selectedSeverity;
  };

  return (
    <div className="mb-4">
      <p className="text-sm text-gray-600 mb-2 italic">
        Click on the matrix to select likelihood and severity. <button 
          type="button"
          onClick={() => {
            setShowLikelihoodDesc(!showLikelihoodDesc);
            setShowSeverityDesc(!showSeverityDesc);
          }}
          className="text-purple-600 hover:text-purple-800 underline font-medium"
        >
          {(showLikelihoodDesc || showSeverityDesc) ? 'Hide descriptions' : 'Click here to see descriptions below'}
        </button>
      </p>
      <div className="flex gap-6 items-start">
        {/* Matrix Table */}
        <div className="flex-1">
          <div className="bg-white border-2 border-gray-300 rounded-xl shadow-md overflow-hidden">
            <table className="w-full table-fixed">
              <thead>
                <tr>
                  <th className="bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '50px' }}></th>
                  <th className="bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '110px' }}></th>
                  <th colSpan={5} className="py-4 px-2 text-center border-b-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100">
                    <span className="text-base font-bold text-gray-900 uppercase tracking-wider">← Severity →</span>
                  </th>
                </tr>
                <tr>
                  <th className="border-r-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '50px' }}></th>
                  <th className="border-r-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100" style={{ width: '110px', height: '90px' }}>
                  </th>
                  {severityLabels.map((sev) => (
                    <th key={sev} className="border-b border-l border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 px-2" style={{ height: '90px' }}>
                      <div className="flex items-center justify-center h-full">
                        <div className="transform -rotate-45 origin-center whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-800">{sev}</span>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {likelihoodLabels.map((lik, likIdx) => (
                  <tr key={lik} className="group">
                    {likIdx === 0 && (
                      <td rowSpan={5} className="border-r-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 text-center align-middle" style={{ width: '50px' }}>
                        <div className="flex items-center justify-center h-full">
                          <div className="transform -rotate-90 whitespace-nowrap">
                            <span className="text-base font-bold text-gray-900 uppercase tracking-wider">← Likelihood →</span>
                          </div>
                        </div>
                      </td>
                    )}
                    <td className="border-r-2 border-t border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 text-right" style={{ height: '70px', width: '110px' }}>
                      <div className="flex items-center justify-end h-full px-2">
                        <span className="text-sm font-semibold text-gray-900 text-right leading-tight" style={{ wordBreak: 'break-word', maxWidth: '100px' }}>{lik}</span>
                      </div>
                    </td>
                    {severityLabels.map((sev, sevIdx) => (
                      <td key={`${lik}-${sev}`} className="p-2 border border-gray-300 bg-white group-hover:bg-gray-50/30 transition-colors" style={{ height: '70px' }}>
                        <div className="flex items-center justify-center h-full">
                          <button
                            type="button"
                            onClick={() => onSelect(lik, sev)}
                            className={`
                              w-12 h-12 rounded-xl transition-all duration-200 cursor-pointer
                              flex items-center justify-center
                              hover:scale-110 hover:shadow-2xl hover:z-10 active:scale-95
                              ${getRiskColorForCell(likIdx, sevIdx)}
                              ${isSelected(likIdx, sevIdx) ? 'ring-4 ring-offset-2 ring-purple-500 ring-opacity-60' : ''}
                            `}
                            title={`${lik} x ${sev} = ${(likIdx + 1) * (sevIdx + 1)}`}
                          />
                        </div>
                      </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* RAG Key */}
        <div className="flex-shrink-0 w-56">
          <div className="bg-white border-2 border-gray-300 rounded-xl shadow-md p-5 sticky top-4">
            <h5 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Risk Level Key</h5>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-xl shadow-lg flex-shrink-0"></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">Low</p>
                  <p className="text-xs text-gray-600">Green</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-xl shadow-lg flex-shrink-0"></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">Medium</p>
                  <p className="text-xs text-gray-600">Amber</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-xl shadow-lg flex-shrink-0"></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">High</p>
                  <p className="text-xs text-gray-600">Red</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Likelihood Descriptions */}
      {showLikelihoodDesc && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h5 className="text-xs font-semibold text-gray-700 mb-2">Likelihood Definitions:</h5>
          <div className="space-y-1">
            {matrixSnap.infoSec.likelihoodLabels.map((item) => (
              <div key={item.label} className="text-xs">
                <span className="font-medium text-gray-700">{item.label}:</span>{' '}
                <span className="text-gray-600">{item.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Severity Descriptions */}
      {showSeverityDesc && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h5 className="text-xs font-semibold text-gray-700 mb-2">Severity Definitions:</h5>
          <div className="space-y-1">
            {matrixSnap.infoSec.impactLabels.map((item) => (
              <div key={item.label} className="text-xs">
                <span className="font-medium text-gray-700">{item.label}:</span>{' '}
                <span className="text-gray-600">{item.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedLikelihood && selectedSeverity && (
        <p className="text-sm text-gray-600 mt-3">
          Selected: <span className="font-medium">{selectedLikelihood}</span> x <span className="font-medium">{selectedSeverity}</span> = {
            likelihoodValues[selectedLikelihood] * severityValues[selectedSeverity]
          }
        </p>
      )}
    </div>
  );
}

function RiskAssessmentModule() {
  const riskSnap = useSnapshot(riskStore);
  const assetSnap = useSnapshot(assetStore);
  const adminSnap = useSnapshot(adminStore);
  const globalSnap = useSnapshot(globalConfigStore);
  const { showToast, ToastContainer } = useToast();
  
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssetListModal, setShowAssetListModal] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [viewingRisk, setViewingRisk] = useState<Risk | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState('all');

  // Load dynamic data from admin store
  const treatmentOptions = useMemo(() =>
    globalSnap.treatmentStrategies.map(t => ({
      value: t.name,
      label: t.name,
      description: t.description
    })),
    [globalSnap.treatmentStrategies]
  );

  const riskCategories = useMemo(() =>
    globalSnap.riskCategories.map(r => r.name),
    [globalSnap.riskCategories]
  );

  // Control Status and Review Periods from adminStore
  const controlStatusOptions = useMemo(() =>
    adminSnap.controlStatusOptions.filter(s => s !== 'Not Applicable'),
    [adminSnap.controlStatusOptions]
  );

  const reviewPeriodOptions = useMemo(() => 
    adminSnap.reviewPeriodOptions,
    [adminSnap.reviewPeriodOptions]
  );

  // Get dynamic likelihood and severity labels from riskMatrixStore
  const matrixSnap = useSnapshot(riskMatrixStore);
  const likelihoodOptions = useMemo(() => 
    matrixSnap.infoSec.likelihoodLabels.map(l => l.label),
    [matrixSnap.infoSec.likelihoodLabels]
  );

  const severityOptions = useMemo(() => 
    matrixSnap.infoSec.impactLabels.map(l => l.label),
    [matrixSnap.infoSec.impactLabels]
  );

  const defaultTreatment = treatmentOptions.length > 0 ? treatmentOptions[0].value : 'Treat';
  const defaultRiskCategory = riskCategories.length > 0 ? [riskCategories[0]] : ['Confidentiality'];
  const defaultLikelihood = likelihoodOptions.length > 0 ? likelihoodOptions[2] : 'Possible'; // Middle value
  const defaultSeverity = severityOptions.length > 0 ? severityOptions[2] : 'Moderate'; // Middle value

  const initialFormData: RiskFormData = {
    referenceNumber: '', assetCategory: '', asset: '', assetReferenceNumber: '', assetGroup: '', assetGroupCount: 0, assetLinkType: 'group' as const, threat: '', vulnerability: '',
    riskOwner: '', riskCategory: defaultRiskCategory, ucLikelihood: defaultLikelihood, ucSeverity: defaultSeverity, treatment: defaultTreatment,
    treatmentDetail: '', managedLikelihood: likelihoodOptions[1] || 'Unlikely', managedSeverity: severityOptions[1] || 'Minor',
    controls: [{ control: '', controlImplementation: '' }],
    opportunities: '', actions: '', comments: '',
    controlStatus: controlStatusOptions[0] || 'Not Started', dateCompleted: '', maxReviewPeriod: reviewPeriodOptions[2]?.value || '12', lastDateReviewed: '', notes: '',
  };

  const [formData, setFormData] = useState<RiskFormData>(initialFormData);
  const [threatInputMode, setThreatInputMode] = useState<'dropdown' | 'freetext'>('dropdown');

  // Owner filter uses risk owner values from the risks themselves
  const riskOwners = useMemo(() => [...new Set(riskSnap.risks.map((r) => r.riskOwner).filter(Boolean))].sort(), [riskSnap.risks]);

  const filteredRisks = useMemo(() => {
    return riskSnap.risks.filter((risk) => {
      const matchesSearch = !riskSnap.searchTerm || risk.asset.toLowerCase().includes(riskSnap.searchTerm.toLowerCase()) ||
        risk.threat.toLowerCase().includes(riskSnap.searchTerm.toLowerCase()) || risk.referenceNumber?.toLowerCase().includes(riskSnap.searchTerm.toLowerCase());
      const matchesCategory = riskSnap.categoryFilter === 'all' || risk.assetCategory === riskSnap.categoryFilter;
      const matchesStatus = riskSnap.statusFilter === 'all' || risk.controlStatus === riskSnap.statusFilter;
      const matchesOwner = ownerFilter === 'all' || risk.riskOwner === ownerFilter;
      return matchesSearch && matchesCategory && matchesStatus && matchesOwner;
    });
  }, [riskSnap.risks, riskSnap.searchTerm, riskSnap.categoryFilter, riskSnap.statusFilter, ownerFilter]);

  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const sortedRisks = useMemo(() => {
    if (!sortColumn) return filteredRisks;
    return [...filteredRisks].sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortColumn) {
        case 'reference': aVal = a.referenceNumber || ''; bVal = b.referenceNumber || ''; break;
        case 'asset': aVal = a.asset || a.assetCategory || ''; bVal = b.asset || b.assetCategory || ''; break;
        case 'threat': aVal = a.threat || ''; bVal = b.threat || ''; break;
        case 'control': aVal = a.controls?.[0]?.control || a.control || ''; bVal = b.controls?.[0]?.control || b.control || ''; break;
        case 'uncontrolled': aVal = calculateRiskScore(a.ucLikelihood, a.ucSeverity).score; bVal = calculateRiskScore(b.ucLikelihood, b.ucSeverity).score; break;
        case 'managed': aVal = calculateRiskScore(a.managedLikelihood, a.managedSeverity).score; bVal = calculateRiskScore(b.managedLikelihood, b.managedSeverity).score; break;
        case 'status': aVal = a.controlStatus || ''; bVal = b.controlStatus || ''; break;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filteredRisks, sortColumn, sortDirection]);

  const [selectedAssetGroup, setSelectedAssetGroup] = useState('');
  const [preFilledFromTile, setPreFilledFromTile] = useState(false);
  const [showGroupListModal, setShowGroupListModal] = useState(false);
  const [showCategoryTiles, setShowCategoryTiles] = useState(false);

  // Category stats for summary tiles (original style)
  const categoryStats = useMemo(() => {
    return Object.keys(iso27001Categories).map((category) => {
      const categoryAssets = assetSnap.assets.filter((a) => a.iso27001Category === category);
      const assessedGroups = new Set(riskSnap.risks.filter((r) => r.assetCategory === category && r.assetGroup).map((r) => r.assetGroup));
      const groupMap = new Map<string, typeof categoryAssets>();
      categoryAssets.forEach(asset => {
        if (asset.assetGroup) {
          const existing = groupMap.get(asset.assetGroup) || [];
          existing.push(asset);
          groupMap.set(asset.assetGroup, existing);
        }
      });
      const groups = Array.from(groupMap.entries()).map(([groupName, assets]) => ({
        groupName,
        count: assets.length,
        existingRisks: riskSnap.risks.filter(r => r.assetCategory === category && r.assetGroup === groupName).length,
      })).sort((a, b) => a.groupName.localeCompare(b.groupName));
      const totalGroups = groups.length;
      const assessedGroupCount = groups.filter(g => assessedGroups.has(g.groupName)).length;
      return {
        category,
        total: categoryAssets.length,
        totalGroups,
        assessed: assessedGroupCount,
        unassessed: totalGroups - assessedGroupCount,
        risks: riskSnap.risks.filter((r) => r.assetCategory === category).length,
        groups,
      };
    });
  }, [assetSnap.assets, riskSnap.risks]);

  // Groups for the selected category (shown in group list modal)
  const groupsForCategory = useMemo(() => {
    const stat = categoryStats.find(s => s.category === selectedCategory);
    return stat?.groups || [];
  }, [selectedCategory, categoryStats]);

  // Assets for the selected group + category (shown in asset list modal)
  const assetsForGroupCategory = useMemo(() => {
    if (!selectedCategory || !selectedAssetGroup) return [];
    return assetSnap.assets.filter((a) => a.iso27001Category === selectedCategory && a.assetGroup === selectedAssetGroup);
  }, [selectedCategory, selectedAssetGroup, assetSnap.assets]);

  const handleTileClick = (category: string) => {
    setSelectedCategory(category);
    setShowGroupListModal(true);
  };

  const handleGroupSelect = (groupName: string) => {
    setSelectedAssetGroup(groupName);
    setShowGroupListModal(false);
    setShowAssetListModal(true);
  };

  const handleAssessGroup = () => {
    setShowAssetListModal(false);
    setEditingRisk(null);
    setPreFilledFromTile(true);
    setThreatInputMode('dropdown');
    const count = assetsForGroupCategory.length;
    setFormData({
      ...initialFormData,
      assetCategory: selectedCategory,
      assetGroup: selectedAssetGroup,
      assetGroupCount: count,
      assetLinkType: 'group' as const,
      asset: '',
      assetReferenceNumber: '',
    });
    setShowModal(true);
  };

  const openAddModal = () => { setEditingRisk(null); setPreFilledFromTile(false); setThreatInputMode('dropdown'); setFormData(initialFormData); setShowModal(true); };
  const openEditModal = (risk: Risk) => {
    setEditingRisk(risk); setPreFilledFromTile(false);
    // Build controls array from legacy flat fields if needed
    const controls = (risk.controls && risk.controls.length > 0)
      ? risk.controls
      : risk.control
        ? [{ control: risk.control, customControl: risk.customControl || '', controlImplementation: risk.controlImplementation || '' }]
        : [{ control: '', controlImplementation: '' }];
    setFormData({ ...risk, controls });
    // Auto-detect mode: if threat matches a known dropdown option, use dropdown; otherwise free text
    const knownThreats = getThreatsByCategory(risk.assetCategory);
    setThreatInputMode(risk.threat && !knownThreats.includes(risk.threat) ? 'freetext' : 'dropdown');
    setShowModal(true);
  };
  const openViewModal = (risk: Risk) => { setViewingRisk(risk); setShowViewModal(true); };

  const handleDuplicate = (risk: Risk) => {
    setEditingRisk(null);
    setThreatInputMode('dropdown');
    setFormData({ ...risk,
      referenceNumber: '',
      threat: '',
      vulnerability: '',
      ucLikelihood: defaultLikelihood,
      ucSeverity: defaultSeverity,
      managedLikelihood: likelihoodOptions[1] || 'Unlikely',
      managedSeverity: severityOptions[1] || 'Minor',
      opportunities: '',
      actions: '',
      comments: '',
      controlStatus: 'Not Started',
      dateCompleted: '',
      maxReviewPeriod: reviewPeriodOptions[2]?.value || '12',
      lastDateReviewed: '',
      notes: '',
    });
    setShowModal(true);
    showToast('Risk duplicated - complete the new risk details', 'info');
  };

  const handleSave = async () => {
    if (!formData.assetCategory || !formData.threat || !formData.vulnerability) { showToast('Please fill in required fields', 'error'); return; }
    // Validate controls
    const nonEmptyControls = formData.controls.filter(c => c.control !== '');
    if (nonEmptyControls.length === 0) { showToast('Please select at least one control', 'error'); return; }
    for (const ctrl of nonEmptyControls) {
      if (ctrl.control === 'Custom control' && !ctrl.customControl?.trim()) { showToast('Please enter a name for each custom control', 'error'); return; }
    }
    // Build persist data with backward-compat flat fields from first control
    const persistData = {
      ...formData,
      controls: nonEmptyControls,
      // Backward-compat flat fields (populated from first control)
      control: nonEmptyControls[0]?.control || '',
      customControl: nonEmptyControls[0]?.customControl || '',
      controlImplementation: nonEmptyControls[0]?.controlImplementation || '',
    };
    try {
      if (editingRisk) {
        await riskActions.updateRisk(editingRisk.id, persistData);
        showToast('Risk updated successfully', 'success');
      } else {
        const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const newRisk: Risk = {
          ...persistData,
          id: tempId,
          referenceNumber: '',
          riskTitle: formData.riskTitle || '',
          riskDescription: formData.riskDescription || '',
          assetGroup: formData.assetGroup || '',
          assetGroupCount: formData.assetGroupCount || 0,
          assetLinkType: formData.assetLinkType || 'group',
          ucImpact: formData.ucImpact || formData.ucSeverity,
          ucRiskLevel: '',
          ucRiskScore: 0,
          managedImpact: formData.managedImpact || formData.managedSeverity,
          managedRiskLevel: '',
          managedRiskScore: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString()
        };
        await riskActions.addRisk(newRisk);
        showToast('Risk created successfully', 'success');
      }
      setShowModal(false);
    } catch (e: unknown) {
      const err = e as Error & { status?: number };
      if (err.status === 409) {
        showToast('This risk was modified by another user. The latest version has been loaded.', 'error');
      } else {
        showToast('Failed to save risk — please try again', 'error');
      }
    }
  };

  const handleDelete = (id: string) => { if (confirm('Are you sure you want to delete this risk?')) { riskActions.deleteRisk(id); showToast('Risk deleted successfully', 'success'); } };
  const handleExport = () => { const data = prepareRiskExportData(filteredRisks as Risk[]); exportToCSV(data, 'risk-assessment'); showToast('Export completed', 'success'); };

  const calculateReviewByDate = (lastReviewed: string, periodMonths: string): string => {
    if (!lastReviewed || !periodMonths) return '';
    const date = new Date(lastReviewed); date.setMonth(date.getMonth() + parseInt(periodMonths));
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <NavigationSidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <ModuleHeader title="Information Security Risk Assessment" subtitle="Comprehensive risk management and treatment planning" />
        <div className="flex-shrink-0 px-6 pt-4 pb-0">
        {/* Action Bar + Stats */}
        <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button onClick={openAddModal} icon="Plus">Add Risk</Button>
              <Button variant="secondary" onClick={handleExport} icon="Download">Export CSV</Button>
              <div className="hidden md:flex items-center gap-3 ml-2 pl-3 border-l border-gray-200">
                <button onClick={() => riskActions.setStatusFilter("all")} className={`text-center px-2 py-1 rounded transition ${riskSnap.statusFilter === 'all' ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                  <p className="text-xs text-gray-500">Total</p><p className="text-lg font-bold">{riskSnap.risks.length}</p>
                </button>
                <button onClick={() => riskActions.setStatusFilter("Not Started")} className={`text-center px-2 py-1 rounded transition ${riskSnap.statusFilter === 'Not Started' ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                  <p className="text-xs text-gray-500">Not Started</p><p className="text-lg font-bold text-gray-600">{riskSnap.risks.filter((r) => r.controlStatus === "Not Started").length}</p>
                </button>
                <button onClick={() => riskActions.setStatusFilter("In Progress")} className={`text-center px-2 py-1 rounded transition ${riskSnap.statusFilter === 'In Progress' ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                  <p className="text-xs text-gray-500">In Progress</p><p className="text-lg font-bold text-amber-600">{riskSnap.risks.filter((r) => r.controlStatus === "In Progress").length}</p>
                </button>
                <button onClick={() => riskActions.setStatusFilter("Implemented")} className={`text-center px-2 py-1 rounded transition ${riskSnap.statusFilter === 'Implemented' ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                  <p className="text-xs text-gray-500">Implemented</p><p className="text-lg font-bold text-green-600">{riskSnap.risks.filter((r) => r.controlStatus === "Implemented").length}</p>
                </button>
                <button onClick={() => riskActions.setStatusFilter("Under Review")} className={`text-center px-2 py-1 rounded transition ${riskSnap.statusFilter === 'Under Review' ? 'bg-purple-50' : 'hover:bg-gray-50'}`}>
                  <p className="text-xs text-gray-500">Under Review</p><p className="text-lg font-bold text-blue-600">{riskSnap.risks.filter((r) => r.controlStatus === "Under Review").length}</p>
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search risks..." value={riskSnap.searchTerm} onChange={(e) => riskActions.setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
              </div>
              <select value={riskSnap.categoryFilter} onChange={(e) => riskActions.setCategoryFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                <option value="all">All Categories</option>
                {Object.keys(iso27001Categories).map((cat) => (<option key={cat} value={cat}>{cat}</option>))}
              </select>
              <select value={riskSnap.statusFilter} onChange={(e) => riskActions.setStatusFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                <option value="all">All Statuses</option>
                {controlStatusOptions.map((status) => (<option key={status} value={status}>{status}</option>))}
              </select>
              <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                <option value="all">All Owners</option>
                {riskOwners.map((owner) => (<option key={owner} value={owner}>{owner}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* Category Tiles — collapsible */}
        <div className="mb-2">
          <button
            onClick={() => setShowCategoryTiles(!showCategoryTiles)}
            className="flex items-center gap-2 w-full text-left group"
          >
            <svg className={`w-4 h-4 text-gray-500 transition-transform ${showCategoryTiles ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <h2 className="text-sm font-semibold text-gray-700">ISO 27001 Categories</h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{categoryStats.length}</span>
            <span className="text-xs text-gray-400 ml-auto group-hover:text-gray-600">Click to {showCategoryTiles ? 'collapse' : 'expand'}</span>
          </button>
          {showCategoryTiles && (
            <div className="grid grid-cols-4 lg:grid-cols-7 gap-2 mt-2">
              {categoryStats.map((stat, idx) => (
                <motion.div key={stat.category} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                  onClick={() => handleTileClick(stat.category)} className="bg-white rounded-lg shadow-sm p-2.5 cursor-pointer hover:shadow-md transition-shadow border-l-3 border-purple-500">
                  <h3 className="font-semibold text-gray-900 text-xs mb-1.5 leading-tight line-clamp-2">{stat.category}</h3>
                  <div className="space-y-0.5">
                    <div className="flex justify-between text-[11px]"><span className="text-gray-400">Groups:</span><span className="font-medium">{stat.totalGroups}</span></div>
                    <div className="flex justify-between text-[11px]"><span className="text-gray-400">Assessed:</span><span className="font-medium text-green-600">{stat.assessed}</span></div>
                    <div className="flex justify-between text-[11px]"><span className="text-gray-400">Unassessed:</span><span className="font-medium text-amber-600">{stat.unassessed}</span></div>
                    <div className="flex justify-between text-[11px] pt-0.5 border-t border-gray-100"><span className="text-gray-400">Risks:</span><span className="font-medium text-purple-600">{stat.risks}</span></div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        </div>

        {/* Risks Table — fills remaining space, scrolls independently */}
        <div className="flex-1 min-h-0 px-6 pb-6">
          <div className="bg-white rounded-lg shadow-md h-full overflow-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {([
                    { key: 'reference', label: 'Reference' },
                    { key: 'asset', label: 'Asset' },
                    { key: 'threat', label: 'Threat' },
                    { key: 'control', label: 'Control' },
                    { key: 'uncontrolled', label: 'Uncontrolled' },
                    { key: 'managed', label: 'Managed' },
                    { key: 'status', label: 'Status' },
                  ] as const).map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-600 cursor-pointer select-none hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sortColumn === col.key ? (
                          <svg className="w-3.5 h-3.5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            {sortDirection === 'asc'
                              ? <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 6.414l-3.293 3.293a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              : <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 13.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />}
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedRisks.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No risks found</td></tr>
                ) : (
                  sortedRisks.map((risk) => {
                    const ucRisk = calculateRiskScore(risk.ucLikelihood, risk.ucSeverity);
                    const mRisk = calculateRiskScore(risk.managedLikelihood, risk.managedSeverity);
                    return (
                      <tr key={risk.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-purple-600">{risk.referenceNumber}</td>
                        <td className="px-4 py-3"><p className="font-medium text-gray-900 text-sm">{risk.assetLinkType === 'group' && risk.assetGroup ? `${risk.assetGroup} (${risk.assetGroupCount || 0})` : risk.asset || risk.assetCategory}</p><p className="text-xs text-gray-500">{risk.assetCategory}</p></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{risk.threat}</td>
                        <td className="px-4 py-3">
                          {(() => {
                            const controls = risk.controls?.length > 0 ? risk.controls : risk.control ? [{ control: risk.control }] : [];
                            const first = controls[0]?.control;
                            if (!first) return <span className="text-xs text-gray-400">—</span>;
                            const label = first.length > 20 ? first.substring(0, 20) + '…' : first;
                            return (
                              <div>
                                <span className="text-xs text-gray-700">{label}</span>
                                {controls.length > 1 && (
                                  <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-700 rounded-full">+{controls.length - 1}</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3"><span className="px-2 py-1 rounded text-xs font-medium text-white" style={{ backgroundColor: ucRisk.color }}>{ucRisk.level} ({ucRisk.score})</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-1 rounded text-xs font-medium text-white" style={{ backgroundColor: mRisk.color }}>{mRisk.level} ({mRisk.score})</span></td>
                        <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-medium ${risk.controlStatus === "Implemented" ? "bg-green-100 text-green-800" : risk.controlStatus === "In Progress" ? "bg-amber-100 text-amber-800" : risk.controlStatus === "Under Review" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}`}>{risk.controlStatus}</span></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openViewModal(risk as Risk)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition" title="View"><Icon name="Eye" size={16} /></button>
                            <button onClick={() => openEditModal(risk as Risk)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition" title="Edit"><Icon name="Edit" size={16} /></button>
                            <button onClick={() => handleDuplicate(risk as Risk)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition" title="Duplicate"><Icon name="Copy" size={16} /></button>
                            <button onClick={() => handleDelete(risk.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition" title="Delete"><Icon name="Trash" size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Asset Group List Modal — shows groups within a category */}
      <Modal isOpen={showGroupListModal} onClose={() => setShowGroupListModal(false)} title={selectedCategory} subtitle='Select an asset group to assess' size='lg' gradient='from-purple-600 to-indigo-600'>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {groupsForCategory.length === 0 ? (<p className="text-center text-gray-500 py-8">No asset groups in this category</p>) : (
            groupsForCategory.map((group) => {
              const hasRisks = group.existingRisks > 0;
              return (
                <div key={group.groupName} onClick={() => handleGroupSelect(group.groupName)}
                  className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition ${hasRisks ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-purple-300'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900">{group.groupName}</p>
                      <p className="text-sm text-gray-500">{group.count} {group.count === 1 ? 'asset' : 'assets'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasRisks ? (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{group.existingRisks} {group.existingRisks === 1 ? 'risk' : 'risks'}</span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Not assessed</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal>

        {/* Asset List Modal — shows assets within a group */}
      <Modal isOpen={showAssetListModal} onClose={() => setShowAssetListModal(false)} title={`${selectedCategory} — ${selectedAssetGroup}`} subtitle={`${assetsForGroupCategory.length} ${assetsForGroupCategory.length === 1 ? 'asset' : 'assets'} in this group`} size='lg' gradient='from-purple-600 to-indigo-600'>
        <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
          {assetsForGroupCategory.length === 0 ? (<p className="text-center text-gray-500 py-8">No assets in this group</p>) : (
            assetsForGroupCategory.map((asset) => {
              const displayName = getAssetDisplayName(asset);
              return (
                <div key={asset.id} className="p-3 rounded-lg border bg-white border-gray-200">
                  <div className="flex justify-between items-center">
                    <div><p className="font-medium text-gray-900 text-sm">{displayName}</p><p className="text-xs text-gray-500">{asset.referenceNumber} • {asset.location || 'No location'}</p></div>
                    <span className="text-xs text-gray-500">{asset.owner || 'No owner'}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {assetsForGroupCategory.length > 0 && (
          <button
            onClick={handleAssessGroup}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md"
          >
            Assess this Group ({assetsForGroupCategory.length} {assetsForGroupCategory.length === 1 ? 'asset' : 'assets'})
          </button>
        )}
      </Modal>

      {/* Add/Edit Risk Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingRisk ? 'Edit Risk' : 'Add New Risk'} subtitle={editingRisk?.referenceNumber} size='xl' gradient='from-red-600 to-pink-600'>
        <RiskForm
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
          onCancel={() => setShowModal(false)}
          isEditing={!!editingRisk}
          assets={assetSnap.assets as Asset[]}
          calculateReviewByDate={calculateReviewByDate}
          treatmentOptions={treatmentOptions}
          riskCategories={riskCategories}
          likelihoodOptions={likelihoodOptions}
          severityOptions={severityOptions}
          controlStatusOptions={[...controlStatusOptions]}
          reviewPeriodOptions={[...reviewPeriodOptions]}
          preFilledFromTile={preFilledFromTile}
          threatInputMode={threatInputMode}
          setThreatInputMode={setThreatInputMode}
        />
      </Modal>

      {/* View Risk Modal */}
      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title={'Risk Details - ' + (viewingRisk?.referenceNumber || '')} size='xl' gradient='from-gray-600 to-gray-700'>
        {viewingRisk && <RiskDetails risk={viewingRisk} />}
      </Modal>

        <ToastContainer />
      </div>
    </div>
  );
}

// Risk Form Component
function RiskForm({
  formData,
  setFormData,
  onSave,
  onCancel,
  isEditing,
  assets,
  calculateReviewByDate,
  treatmentOptions,
  riskCategories,
  likelihoodOptions,
  severityOptions,
  controlStatusOptions,
  reviewPeriodOptions,
  preFilledFromTile = false,
  threatInputMode,
  setThreatInputMode
}: {
  formData: RiskFormData;
  setFormData: (data: RiskFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  isEditing: boolean;
  assets: Asset[];
  calculateReviewByDate: (lastReviewed: string, periodMonths: string) => string;
  treatmentOptions: Array<{ value: string; label: string; description: string }>;
  riskCategories: string[];
  likelihoodOptions: string[];
  severityOptions: string[];
  controlStatusOptions: string[];
  reviewPeriodOptions: Array<{ value: string; label: string }>;
  preFilledFromTile?: boolean;
  threatInputMode: 'dropdown' | 'freetext';
  setThreatInputMode: (mode: 'dropdown' | 'freetext') => void;
}) {
  const adminSnap = useSnapshot(adminStore);
  const globalSnap = useSnapshot(globalConfigStore);
  const assetSnap = useSnapshot(assetStore);
  const [wordingPickerIndex, setWordingPickerIndex] = useState<number | null>(null);

  // Determine if asset fields should be read-only (pre-filled from tile or editing)
  const assetFieldsReadOnly = preFilledFromTile;

  const categoryAssets = useMemo(() => formData.assetCategory ? assets.filter((a) => a.iso27001Category === formData.assetCategory) : [], [formData.assetCategory, assets]);

  // Get asset groups filtered by selected category
  const categoryAssetGroups = useMemo(() => {
    if (!formData.assetCategory) return [];
    const groupMap = new Map<string, number>();
    assetSnap.assets.forEach(a => {
      if (a.iso27001Category === formData.assetCategory && a.assetGroup) {
        groupMap.set(a.assetGroup, (groupMap.get(a.assetGroup) || 0) + 1);
      }
    });
    return Array.from(groupMap.entries()).map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [formData.assetCategory, assetSnap.assets]);
  const availableThreats = useMemo(() => getThreatsByCategory(formData.assetCategory), [formData.assetCategory, globalSnap.threatTypes]);
  const availableVulnerabilities = useMemo(() => getVulnerabilitiesByThreat(formData.threat), [formData.threat, globalSnap.vulnerabilities]);

  // Get controls from global config
  const allControls = useMemo(() =>
    globalSnap.infoSecControls.map(c => ({
      control: c.name,
      section: c.section,
      objective: c.description || ''
    })),
    [globalSnap.infoSecControls]
  );
  
  // Per-control-entry section filter state
  const [controlFilters, setControlFilters] = useState<Record<number, string>>({});

  const getFilteredControls = (index: number) => {
    const filter = controlFilters[index] || '';
    if (!filter) return allControls;
    return allControls.filter(c => c.section === filter);
  };

  const setControlFilter = (index: number, section: string) => {
    setControlFilters(prev => ({ ...prev, [index]: section }));
  };

  // Get default implementation text for a control
  const getDefaultImplementation = (controlName: string): string => {
    // Priority 1: Standard wording that specifically matches this control
    const wording = getImplementationWording(controlName);
    const specific = wording.find(w => w.controls.length > 0);
    if (specific) return specific.text;
    // Priority 2: Control's own default implementation text (set by super admin)
    const controlEntry = globalSnap.infoSecControls.find(c => c.name === controlName);
    if (controlEntry?.defaultImplementation) return controlEntry.defaultImplementation;
    // Priority 3: Generic fallback
    return `${controlName} implemented with regular monitoring and review processes.`;
  };

  // Multi-control helpers
  const updateControl = (index: number, updates: Partial<RiskControl>) => {
    const newControls = [...formData.controls];
    newControls[index] = { ...newControls[index], ...updates };
    setFormData({ ...formData, controls: newControls });
  };

  const addControl = () => {
    setFormData({ ...formData, controls: [...formData.controls, { control: '', controlImplementation: '' }] });
  };

  const removeControl = (index: number) => {
    const newControls = formData.controls.filter((_, i) => i !== index);
    setFormData({ ...formData, controls: newControls });
    // If wording picker was open for this or later index, close it
    if (wordingPickerIndex !== null && wordingPickerIndex >= index) {
      setWordingPickerIndex(null);
    }
  };

  const treatmentDescription = useMemo(() => treatmentOptions.find(t => t.value === formData.treatment)?.description || '', [formData.treatment, treatmentOptions]);
  const reviewByDate = calculateReviewByDate(formData.lastDateReviewed, formData.maxReviewPeriod);

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Asset Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Asset Information</h3>

        {/* Legacy single-asset info for editing old risks */}
        {isEditing && formData.assetLinkType === 'single' && formData.asset && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-600 font-medium mb-1">Originally linked to single asset</p>
            <p className="text-sm text-blue-900">{formData.asset}</p>
          </div>
        )}

        {/* Asset Category */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Asset Category <span className="text-red-500">*</span></label>
          {assetFieldsReadOnly ? (
            <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-700">{formData.assetCategory || 'Not selected'}</div>
          ) : (
            <select value={formData.assetCategory} onChange={(e) => {
              const newCategory = e.target.value;
              // Auto-compute count if group already selected
              const newCount = formData.assetGroup ? getGroupCategoryCount(formData.assetGroup, newCategory) : 0;
              setFormData({ ...formData, assetCategory: newCategory, assetGroup: '', assetGroupCount: 0, threat: '', vulnerability: '' });
            }} className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500'>
              <option value="">Select category</option>
              {assetCategoryOptions.map((cat) => (<option key={cat} value={cat}>{cat}</option>))}
            </select>
          )}
        </div>

        {/* Asset Group */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Asset Group</label>
            {assetFieldsReadOnly ? (
              <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-700">{formData.assetGroup || 'None'}</div>
            ) : (
              <input
                type="text"
                value={formData.assetGroup || ''}
                onChange={(e) => {
                  const groupName = e.target.value;
                  const count = groupName ? getGroupCategoryCount(groupName, formData.assetCategory) : 0;
                  setFormData({ ...formData, assetGroup: groupName, assetGroupCount: count, assetLinkType: 'group' });
                }}
                className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500'
                disabled={!formData.assetCategory}
                placeholder="e.g. Laptops, Servers, Network Equipment"
              />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Group Count</label>
            <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600">
              {formData.assetGroupCount || 0} {(formData.assetGroupCount || 0) === 1 ? 'asset' : 'assets'}
            </div>
          </div>
        </div>
      </div>

      {/* Threat & Vulnerability */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Threat & Vulnerability</h3>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button type="button" onClick={() => { if (threatInputMode !== 'dropdown') { setThreatInputMode('dropdown'); setFormData({ ...formData, threat: '', vulnerability: '' }); } }} className={`px-3 py-1 text-xs font-medium transition ${threatInputMode === 'dropdown' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-purple-50'}`}>
              Select from list
            </button>
            <button type="button" onClick={() => { if (threatInputMode !== 'freetext') { setThreatInputMode('freetext'); setFormData({ ...formData, threat: '', vulnerability: '' }); } }} className={`px-3 py-1 text-xs font-medium transition ${threatInputMode === 'freetext' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 hover:bg-purple-50'}`}>
              Enter manually
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {threatInputMode === 'dropdown' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Threat <span className="text-red-500">*</span></label>
                <select value={formData.threat} onChange={(e) => setFormData({ ...formData, threat: e.target.value, vulnerability: '' })} className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500' disabled={!formData.assetCategory}>
                  <option value="">Select threat</option>
                  {availableThreats.map((threat) => (<option key={threat} value={threat}>{threat}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vulnerability <span className="text-red-500">*</span></label>
                <select value={formData.vulnerability} onChange={(e) => setFormData({ ...formData, vulnerability: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" disabled={!formData.threat}>
                  <option value="">Select vulnerability</option>
                  {availableVulnerabilities.map((vuln) => (<option key={vuln} value={vuln}>{vuln}</option>))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Threat <span className="text-red-500">*</span></label>
                <input type="text" value={formData.threat} onChange={(e) => setFormData({ ...formData, threat: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Enter threat description" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vulnerability <span className="text-red-500">*</span></label>
                <input type="text" value={formData.vulnerability} onChange={(e) => setFormData({ ...formData, vulnerability: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Enter vulnerability description" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Risk Owner & Category */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Risk Classification</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Risk Owner</label>
            <input type="text" value={formData.riskOwner} onChange={(e) => setFormData({ ...formData, riskOwner: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Person responsible" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Risk Category</label>
            <div className="space-y-2">
              {riskCategories.map((cat) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.riskCategory.includes(cat)}
                    onChange={(e) => {
                      const updated = e.target.checked
                        ? [...formData.riskCategory, cat]
                        : formData.riskCategory.filter(c => c !== cat);
                      setFormData({ ...formData, riskCategory: updated });
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{cat}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Uncontrolled Risk */}
      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
        <h3 className="text-sm font-semibold text-red-700 mb-3">Uncontrolled Risk Assessment</h3>
        <p className="text-xs text-gray-500 mb-3">Click on the matrix to select likelihood and impact</p>
        <InfoSecRiskMatrix
          selectedLikelihood={formData.ucLikelihood}
          selectedSeverity={formData.ucSeverity}
          onSelect={(likelihood, severity) => setFormData({
            ...formData,
            ucLikelihood: likelihood as Likelihood,
            ucSeverity: severity as Severity
          })}
          title="Uncontrolled Risk Level"
          accentColor="red-600"
        />
        <div className="mt-3 flex items-center gap-2">
          <span className="text-sm text-gray-600">Risk Level:</span>
          {(() => { 
            const risk = calculateRiskScore(formData.ucLikelihood, formData.ucSeverity); 
            return (
              <span className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{ backgroundColor: risk.color }}>
                {risk.level} ({risk.score})
              </span>
            ); 
          })()}
        </div>
      </div>

      {/* Treatment Strategy */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Treatment Strategy</h3>
        <select value={formData.treatment} onChange={(e) => setFormData({ ...formData, treatment: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 mb-2">
          {treatmentOptions.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
        </select>
        <p className="text-sm text-gray-600 italic">{treatmentDescription}</p>
      </div>

      {/* Information Security Controls */}
      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-purple-700">
            Information Security Controls
            <span className="text-xs text-gray-600 ml-2">({allControls.length} controls available)</span>
          </h3>
          <div className="flex items-center gap-2">
            {adminSnap.aiAssistEnabled && (
              <AiControlSuggest
                context={{
                  assetCategory: formData.assetCategory,
                  threat: formData.threat,
                  vulnerability: formData.vulnerability,
                }}
                allControls={allControls}
                existingControls={formData.controls.map(c => c.control).filter(Boolean)}
                onSelectControls={(controlNames) => {
                  // Add new control entries for each selected control with auto-populated implementation
                  const existing = formData.controls.filter(c => c.control !== '');
                  const newEntries = controlNames
                    .filter(name => !existing.some(c => c.control === name))
                    .map(name => ({ control: name, controlImplementation: getDefaultImplementation(name) }));
                  if (newEntries.length > 0) {
                    setFormData({ ...formData, controls: [...existing, ...newEntries] });
                  }
                }}
              />
            )}
          </div>
        </div>

        {/* Control entries */}
        <div className="space-y-4">
          {formData.controls.map((ctrl, index) => {
            const sectionFilter = controlFilters[index] || '';
            const filtered = sectionFilter ? allControls.filter(c => c.section === sectionFilter) : allControls;
            return (
            <div key={index} className="bg-white p-4 rounded-lg border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide">Control {index + 1}</span>
                {formData.controls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeControl(index)}
                    className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                    title="Remove this control"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>

              {/* Per-entry section filter tabs */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                <button type="button" onClick={() => setControlFilter(index, '')} className={`px-2.5 py-1 rounded text-xs font-medium transition ${!sectionFilter ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-purple-100"}`}>
                  All ({allControls.length})
                </button>
                {['Organisational', 'People', 'Physical', 'Technological'].map((section) => {
                  const count = allControls.filter(c => c.section === section).length;
                  return (
                    <button
                      key={section}
                      type="button"
                      onClick={() => setControlFilter(index, section)}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition ${sectionFilter === section ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-purple-100"}`}
                    >
                      {section} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Control dropdown */}
              <select
                value={ctrl.control}
                onChange={(e) => {
                  const newControl = e.target.value;
                  const isCustom = newControl === 'Custom control';
                  // Auto-populate implementation when selecting a control
                  const autoImpl = newControl && !isCustom ? getDefaultImplementation(newControl) : '';
                  updateControl(index, {
                    control: newControl,
                    customControl: isCustom ? ctrl.customControl : '',
                    controlImplementation: autoImpl,
                  });
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select control ({filtered.length} available)</option>
                {filtered.map((c) => (
                  <option key={c.control} value={c.control}>
                    {c.control}{c.objective ? ` - ${c.objective.substring(0, 60)}...` : ''}
                  </option>
                ))}
              </select>

              {/* Custom control name */}
              {ctrl.control === 'Custom control' && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Control Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={ctrl.customControl || ''}
                    onChange={(e) => updateControl(index, { customControl: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Monthly access review, Quarterly penetration testing"
                  />
                  <p className="text-xs text-gray-500 mt-1">Name your organisation-specific control — this will appear in the SoA RACI matrix</p>
                </div>
              )}

              {/* Implementation */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Implementation</label>
                  <div className="flex items-center gap-2">
                    {adminSnap.aiAssistEnabled && ctrl.control && (
                      <AiSuggestButton
                        type="soa-implementation"
                        context={{
                          control: ctrl.control,
                          objective: allControls.find(c => c.control === ctrl.control)?.objective || '',
                          threat: formData.threat,
                          vulnerability: formData.vulnerability,
                        }}
                        onSuggestion={(text) => updateControl(index, { controlImplementation: text })}
                        label="AI Suggest"
                      />
                    )}
                    {/* Help with Wording button hidden for now
                    <button
                      type="button"
                      onClick={() => setWordingPickerIndex(index)}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                    >
                      <Icon name="FileText" size={14} />
                      Help with Wording
                    </button>
                    */}
                  </div>
                </div>
                <textarea
                  value={ctrl.controlImplementation}
                  onChange={(e) => updateControl(index, { controlImplementation: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Describe how this control is implemented..."
                />
              </div>
            </div>
            );
          })}
        </div>

        {/* Add control button */}
        <button
          type="button"
          onClick={addControl}
          className="mt-3 w-full px-4 py-2 border-2 border-dashed border-purple-300 rounded-lg text-sm font-medium text-purple-600 hover:bg-purple-100 hover:border-purple-400 transition flex items-center justify-center gap-1"
        >
          <Icon name="Plus" size={14} />
          Add Control
        </button>

        {/* Help with Wording Picker Modal */}
        {wordingPickerIndex !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
              <div className="p-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-lg">
                <h3 className="text-lg font-semibold">Select Standard Implementation Wording</h3>
                <p className="text-sm text-purple-100">
                  {formData.controls[wordingPickerIndex]?.control
                    ? `Showing wording for: ${formData.controls[wordingPickerIndex].control.split(' ')[0]}`
                    : 'Showing all wording (no control selected)'}
                </p>
              </div>
              <div className="p-4 space-y-2 overflow-y-auto flex-1">
                {(() => {
                  const activeControl = formData.controls[wordingPickerIndex]?.control || '';
                  const filteredWording = getImplementationWording(activeControl);
                  if (filteredWording.length === 0) {
                    return (
                      <p className="text-center text-gray-500 py-8">
                        No help wording defined{activeControl ? ` for control ${activeControl.split(' ')[0]}` : ''}. Add entries in Admin → Standard Wording.
                      </p>
                    );
                  }
                  return filteredWording.map((wording) => (
                    <button
                      key={wording.id}
                      type="button"
                      onClick={() => {
                        updateControl(wordingPickerIndex, { controlImplementation: wording.text });
                        setWordingPickerIndex(null);
                      }}
                      className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                    >
                      <p className="font-medium text-gray-900 mb-1">{wording.name}</p>
                      <p className="text-sm text-gray-600">{wording.text}</p>
                      {wording.controls.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {wording.controls.map(c => (
                            <span key={c} className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-600 rounded">
                              {c.split(' ')[0]}
                            </span>
                          ))}
                        </div>
                      )}
                      {wording.controls.length === 0 && (
                        <span className="text-xs text-gray-400 mt-2 block">Applies to all controls</span>
                      )}
                    </button>
                  ));
                })()}
              </div>
              <div className="p-4 border-t">
                <button
                  type="button"
                  onClick={() => setWordingPickerIndex(null)}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Managed Risk */}
      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
        <h3 className="text-sm font-semibold text-green-700 mb-3">Managed Risk (Post-Treatment)</h3>
        <p className="text-xs text-gray-500 mb-3">Risk level after controls are applied</p>
        <InfoSecRiskMatrix
          selectedLikelihood={formData.managedLikelihood}
          selectedSeverity={formData.managedSeverity}
          onSelect={(likelihood, severity) => setFormData({
            ...formData,
            managedLikelihood: likelihood as Likelihood,
            managedSeverity: severity as Severity
          })}
          title="Managed Risk Level"
          accentColor="green-600"
        />
        <div className="mt-3 flex items-center gap-2">
          <span className="text-sm text-gray-600">Risk Level:</span>
          {(() => { 
            const risk = calculateRiskScore(formData.managedLikelihood, formData.managedSeverity); 
            return (
              <span className="px-3 py-1 rounded-full text-sm font-medium text-white" style={{ backgroundColor: risk.color }}>
                {risk.level} ({risk.score})
              </span>
            ); 
          })()}
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h3>
        <div className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Opportunities</label><textarea value={formData.opportunities} onChange={(e) => setFormData({ ...formData, opportunities: e.target.value })} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Any opportunities arising from this risk..." /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Actions</label><textarea value={formData.actions} onChange={(e) => setFormData({ ...formData, actions: e.target.value })} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Actions to be taken..." /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Comments</label><textarea value={formData.comments} onChange={(e) => setFormData({ ...formData, comments: e.target.value })} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Additional comments..." /></div>
        </div>
      </div>

      {/* Control Status */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Control Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select value={formData.controlStatus} onChange={(e) => setFormData({ ...formData, controlStatus: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
              {controlStatusOptions.map((status) => (<option key={status} value={status}>{status}</option>))}
            </select>
          </div>
          {formData.controlStatus === 'Implemented' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Implementation Date</label>
              <input type="date" value={formData.dateCompleted} onChange={(e) => setFormData({ ...formData, dateCompleted: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
            </div>
          )}
        </div>
      </div>

      {/* Review Schedule */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Review Schedule</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Last Date Reviewed</label><input type="date" value={formData.lastDateReviewed} onChange={(e) => setFormData({ ...formData, lastDateReviewed: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" /></div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Review Period</label>
            <select value={formData.maxReviewPeriod} onChange={(e) => setFormData({ ...formData, maxReviewPeriod: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
              {reviewPeriodOptions.map((period) => (
                <option key={period.value} value={period.value}>{period.label}</option>
              ))}
            </select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-2">Review By Date</label><input type="date" value={reviewByDate} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600" /></div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Notes</h3>
        <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Additional notes..." />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t sticky bottom-0 bg-white">
        <Button onClick={onSave} className="flex-1">{isEditing ? "Update Risk" : "Add Risk"}</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// Risk Details Component
function RiskDetails({ risk }: { risk: Risk }) {
  const ucRisk = calculateRiskScore(risk.ucLikelihood, risk.ucSeverity);
  const mRisk = calculateRiskScore(risk.managedLikelihood, risk.managedSeverity);

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div><label className="text-sm font-medium text-gray-500">Asset Category</label><p className="text-gray-900">{risk.assetCategory}</p></div>
        {risk.assetLinkType === 'group' ? (
          <div>
            <label className="text-sm font-medium text-gray-500">Asset Group</label>
            <p className="text-gray-900">{risk.assetGroup || '-'}{risk.assetGroupCount ? ` (${risk.assetGroupCount} assets)` : ''}</p>
          </div>
        ) : (
          <div><label className="text-sm font-medium text-gray-500">Asset</label><p className="text-gray-900">{risk.asset || "-"}</p></div>
        )}
        <div><label className="text-sm font-medium text-gray-500">Threat</label><p className="text-gray-900">{risk.threat}</p></div>
        <div><label className="text-sm font-medium text-gray-500">Vulnerability</label><p className="text-gray-900">{risk.vulnerability}</p></div>
        <div><label className="text-sm font-medium text-gray-500">Risk Owner</label><p className="text-gray-900">{risk.riskOwner || "-"}</p></div>
        <div><label className="text-sm font-medium text-gray-500">Risk Category</label><p className="text-gray-900">{risk.riskCategory.length > 0 ? risk.riskCategory.join(', ') : "-"}</p></div>
      </div>

      <div className="border-t pt-4">
        <h3 className="font-semibold mb-4">Risk Levels</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="text-sm text-gray-500 mb-1">Uncontrolled Risk</p>
            <p className="text-lg font-bold" style={{ color: ucRisk.color }}>{ucRisk.level} ({ucRisk.score})</p>
            <p className="text-sm text-gray-600">{risk.ucLikelihood} x {risk.ucSeverity}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <p className="text-sm text-gray-500 mb-1">Managed Risk</p>
            <p className="text-lg font-bold" style={{ color: mRisk.color }}>{mRisk.level} ({mRisk.score})</p>
            <p className="text-sm text-gray-600">{risk.managedLikelihood} x {risk.managedSeverity}</p>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="font-semibold mb-4">Treatment & Controls</h3>
        <p className="text-purple-900 font-medium bg-purple-50 p-3 rounded mb-3">{risk.treatment}</p>
        {(() => {
          const controls = risk.controls?.length > 0
            ? risk.controls
            : risk.control
              ? [{ control: risk.control, customControl: risk.customControl, controlImplementation: risk.controlImplementation }]
              : [];
          if (controls.length === 0) return <p className="text-sm text-gray-400">No controls assigned</p>;
          return (
            <div className="space-y-3">
              {controls.map((ctrl, idx) => (
                <div key={idx} className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                  <p className="text-purple-900 font-medium text-sm">
                    {ctrl.control === 'Custom control' ? (ctrl.customControl || 'Custom control') : ctrl.control}
                  </p>
                  {ctrl.controlImplementation && (
                    <p className="text-gray-700 text-sm mt-2 bg-white p-2 rounded">{ctrl.controlImplementation}</p>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {(risk.opportunities || risk.actions || risk.comments) && (
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-4">Additional Information</h3>
          {risk.opportunities && (<div className="mb-3"><label className="text-sm font-medium text-gray-500">Opportunities</label><p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.opportunities}</p></div>)}
          {risk.actions && (<div className="mb-3"><label className="text-sm font-medium text-gray-500">Actions</label><p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.actions}</p></div>)}
          {risk.comments && (<div><label className="text-sm font-medium text-gray-500">Comments</label><p className="text-gray-900 bg-gray-50 p-3 rounded mt-1">{risk.comments}</p></div>)}
        </div>
      )}

      <div className="border-t pt-4">
        <h3 className="font-semibold mb-4">Status & Review</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-gray-500">Control Status</label><p className="text-gray-900">{risk.controlStatus}</p></div>
          {risk.dateCompleted && (<div><label className="text-sm font-medium text-gray-500">Implementation Date</label><p className="text-gray-900">{formatDate(risk.dateCompleted)}</p></div>)}
          <div><label className="text-sm font-medium text-gray-500">Last Reviewed</label><p className="text-gray-900">{risk.lastDateReviewed ? formatDate(risk.lastDateReviewed) : "-"}</p></div>
          <div><label className="text-sm font-medium text-gray-500">Review Period</label><p className="text-gray-900">{risk.maxReviewPeriod} months</p></div>
        </div>
      </div>

      {risk.notes && (<div className="border-t pt-4"><h3 className="font-semibold mb-4">Notes</h3><p className="text-gray-900 bg-gray-50 p-3 rounded">{risk.notes}</p></div>)}
    </div>
  );
}
